import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import defaultLogo from "../../assets/logo.png";
/* ---------------- Animations ---------------- */

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

/* ---------------- Styled ---------------- */

const Wrapper = styled.div`
  background: #0b0e14;
  color: #fff;
  overflow-x: hidden;
`;

const Hero = styled(motion.section)`
  position: relative;
  padding: 120px 20px 100px;
  background: radial-gradient(circle at top, #ce8b54, #1f1f1f 60%);
`;

const Glow = styled(motion.div)`
  position: absolute;
  width: 400px;
  height: 400px;
  background: #ce8b54;
  filter: blur(140px);
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0.6;
`;

const HeroContent = styled(motion.div)`
  max-width: 1100px;
  margin: auto;
  text-align: center;

  h1 {
    font-size: 56px;
    line-height: 1.1;
    margin: 20px 0;

    span {
      background: linear-gradient(90deg, #ffd7b5, #ce8b54);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  p {
    font-size: 18px;
    opacity: 0.9;
    max-width: 700px;
    margin: auto;
  }
`;

const logoGlow = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 0.85; }
`;

const LogoWrap = styled.div`
  text-align: center;
  margin-bottom: 28px;
  display: flex;
  justify-content: center;
  align-items: center;

  .logoBox {
    padding: 16px 26px;
    border-radius: 18px;

    /* 🔥 KEY FIX: better contrast against same-colored background */
    background: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(14px);

    border: 1px solid rgba(255, 255, 255, 0.12);

    display: inline-flex;
    align-items: center;
    justify-content: center;

    /* subtle shadow separation */
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  }

  img {
    height: 180px;
    max-width: 260px;
    object-fit: contain;

    /* 🔥 FIX: makes logo visible even on similar background */
    filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.6))
            brightness(1.05)
            contrast(1.1);
  }
`;

const Badge = styled(motion.div)`
  display: inline-block;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: 14px;
  backdrop-filter: blur(10px);
`;

const Actions = styled(motion.div)`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;

  a {
    padding: 14px 32px;
    border-radius: 30px;
    font-weight: 600;
    text-decoration: none;
  }

  .primary {
    background: linear-gradient(90deg, #ce8b54, #a26532);
    color: #fff;
  }

  .ghost {
    border: 1px solid #fff;
    color: #fff;
  }
`;

const FeatureStrip = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 30px;
  background: #111;
  flex-wrap: wrap;
`;

const Feature = styled(motion.div)`
  font-weight: 600;
  opacity: 0.9;
`;

const Section = styled(motion.section)`
  padding: 90px 20px;
`;

const Title = styled(motion.h2)`
  text-align: center;
  font-size: 36px;
  margin-bottom: 40px;
`;

const AboutText = styled(motion.div)`
  max-width: 900px;
  margin: auto;
  text-align: center;
  line-height: 1.8;
  opacity: 0.9;

  p {
    margin-bottom: 18px;
    font-size: 17px;
  }

  strong {
    color: #ffd7b5;
  }
`;

const Cards = styled(motion.div)`
  max-width: 1100px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 28px;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  padding: 35px;
  text-align: center;
  animation: ${float} 6s ease-in-out infinite;

  h3 {
    margin: 12px 0;
  }

  p {
    opacity: 0.8;
  }
`;

const Icon = styled.div`
  font-size: 36px;
`;

const CTA = styled(motion.section)`
  padding: 90px 20px;
  background: linear-gradient(135deg, #ce8b54, #1f1f1f);
  text-align: center;

  h2 {
    font-size: 42px;
    margin-bottom: 10px;
  }

  p {
    opacity: 0.9;
    margin-bottom: 25px;
  }

  a {
    padding: 16px 40px;
    background: #fff;
    color: #000;
    border-radius: 30px;
    font-weight: 700;
    text-decoration: none;
  }
`;

const Footer = styled.footer`
  padding: 40px 20px;
  background: #000;
  text-align: center;
`;

const Links = styled.div`
  display: flex;
  justify-content: center;
  gap: 22px;
  flex-wrap: wrap;

  a {
    color: #aaa;
    text-decoration: none;

    &:hover {
      color: #fff;
    }
  }
`;

const Copy = styled.div`
  margin-top: 15px;
  font-size: 14px;
  opacity: 0.6;
`;

/* ---------------- Component ---------------- */

const Home = ({ appData }) => {
  // 🔝 Scroll to top when page opens
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const logo = appData?.data?.logo || defaultLogo;

  return (
    <Wrapper>
      {/* HERO */}
      <Hero initial="hidden" animate="visible" variants={fadeUp}>
        <Glow animate={{ y: [0, -20, 0] }} transition={{ duration: 6, repeat: Infinity }} />

        <HeroContent variants={stagger}>
          {/* LOGO FROM API */}
          <LogoWrap variants={fadeUp}>
            <div className="logoBox">
              <img
                src={logo}
                alt="Recharge Junction Logo"
                onError={(e) => {
                  e.target.src = defaultLogo;
                }}
              />
            </div>
          </LogoWrap>

          <Badge variants={fadeUp}>⚡ India’s Smart Recharge Platform</Badge>

          <motion.h1 variants={fadeUp}>
            Powering <span>Instant Recharges</span> <br />
            For Businesses & APIs
          </motion.h1>

          <motion.p variants={fadeUp}>
            Mobile, DTH, Data & API-based recharge solutions with industry-leading success rates.
          </motion.p>

          <Actions variants={fadeUp}>
            <Link to="/register" className="primary">Start Free</Link>
            <Link to="/login" className="ghost">Login</Link>
            <Link to="/contact" className="ghost">Talk to Support</Link>
          </Actions>
        </HeroContent>
      </Hero>

      {/* FEATURE STRIP */}
      <FeatureStrip initial="hidden" whileInView="visible" variants={stagger}>
        {["⚡ 99.9% Uptime", "🔐 Secure Wallet", "📊 Live Reports", "🧩 API Ready"].map(
          (item, i) => (
            <Feature key={i} variants={fadeUp}>{item}</Feature>
          )
        )}
      </FeatureStrip>

      {/* ABOUT US (NO CARDS) */}
      <Section initial="hidden" whileInView="visible">
        <Title variants={fadeUp}>About Us</Title>
        <AboutText variants={fadeUp}>
          <p>
            Welcome to <strong>Recharge Junction</strong>. Since the telecommunication
            industry came into being, it has become a vital part of daily life and a
            major communication channel for everyone.
          </p>
          <p>
            Our unified platform makes telecom services easily accessible.
            We are a trusted name in the recharge domain, focused on reliability,
            speed, and service excellence.
          </p>
          <p>
            With <strong>24×7 support</strong>, <strong>fast RESTful APIs</strong>,
            <strong> easy integration</strong>, and support for <strong>all platforms</strong>,
            we help businesses grow without technology barriers.
          </p>
        </AboutText>
      </Section>

      {/* SERVICES (ORIGINAL CARDS KEPT) */}
      <Section initial="hidden" whileInView="visible">
        <Title variants={fadeUp}>What We Offer</Title>
        <Cards variants={stagger}>
          {[
            { icon: "📱", title: "Instant Recharge", desc: "Ultra-fast Mobile, DTH & Data recharges." },
            { icon: "🔌", title: "Recharge APIs", desc: "Plug & play APIs for automation & scale." },
            { icon: "💼", title: "Smart Wallet", desc: "Balance, margins, settlements & controls." },
            { icon: "📈", title: "Advanced Reports", desc: "GST invoices, analytics & reconciliation." },
          ].map((item, i) => (
            <Card key={i} variants={fadeUp}>
              <Icon>{item.icon}</Icon>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </Card>
          ))}
        </Cards>
      </Section>

      {/* CTA */}
      <CTA initial="hidden" whileInView="visible" variants={fadeUp}>
        <h2>Launch Your Recharge Business Today</h2>
        <p>No setup fees • No hidden charges • Full support</p>
        <Link to="/register">Create Account</Link>
      </CTA>

      {/* FOOTER */}
      <Footer>
        <Links>
        <Link to="/services">Services</Link>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-and-conditions">Terms & Conditions</Link>
          <Link to="/refund-policy">Refund & Cancellation</Link>
          <Link to="/contact">Contact Us</Link>
        </Links>
        <Copy>
          ©  {"KAROLS WEB SOLUTION PRIVATE LIMITED 2026. All Rights Reserved"}
        </Copy>
      </Footer>
    </Wrapper>
  );
};

export default Home;